import { ICallerIdService } from '../interfaces/ICallerIdService';
import { CallerIdLineType, ICallerIdResponse, ILineSelectionInfo } from '../types';
import { getLocalStorageItem } from '../../../packages/logger/src';
import { Logger } from '../../../packages/logger/src';
import {
  DISPATCH_STORAGE_EVENTS,
  IntegrationPhoneServicesResponse,
} from '../../../packages/adapter-interfaces/src';
import { getMultiLineLabel } from '../../../packages/widgets/src/utils/handleMultiLineLabel';
import { getPhoneServicesData } from '../../../packages/widgets/src/services/WebexEventListener';

/**
 * Resolve a display title for a Phone Services line.
 *
 * Previously gated by a single org id allowlist; the gate has been removed so
 * every org now sees the user-defined label (when present) preferred over the
 * raw line name. Falls back to name, then a generic "Line N" string.
 */
function getLineTitle(label: string, name: string, fallback: string): string {
  return label || name || fallback;
}

/**
 * Service for WXC multi-line using Phone Services data (WebSocket push).
 *
 * For WXC users with shared/virtual lines, the line list comes from
 * the Phone Services Mercury WebSocket event (integration.phoneservices).
 *
 * Implements ICallerIdService so the hook/UI don't need to know the data source.
 *
 * Data source: localStorage STORAGE_PHONE_SERVICES_DATA (populated by WebexEventListener.ts)
 *
 * Selection is NOT cached across page loads — always defaults to the first line.
 * The Webex Desktop app owns the preferred line and can change it independently
 * (while the web app is closed, etc.), so any cached value would be unreliable.
 * Within a session, React state in useLineSelection keeps the user's selection.
 */
export class PhoneServicesLineService implements ICallerIdService {

  async getLines(): Promise<ICallerIdResponse> {
    let phoneServicesData: IntegrationPhoneServicesResponse | null = null;
    try {
      // CRITICAL: Call getPhoneServicesData() to wait for Mercury listener to populate localStorage
      // This handles: 1) Cache checks, 2) Mercury listener setup, 3) Timeout fallback
      phoneServicesData = await getPhoneServicesData();
      if (phoneServicesData?.data?.payload?.lines?.length) {
        Logger.info(`[LineSelection] PhoneServicesLineService — received ${phoneServicesData.data.payload.lines.length} lines from getPhoneServicesData()`);
      } else {
        Logger.info('[LineSelection] PhoneServicesLineService — getPhoneServicesData() returned empty lines or timed out');
      }
    } catch (err) {
      Logger.error(`[LineSelection] PhoneServicesLineService — failed to get Phone Services data: ${err}`);
      // Fallback to localStorage as last resort
      phoneServicesData = getLocalStorageItem<IntegrationPhoneServicesResponse>(
        DISPATCH_STORAGE_EVENTS.STORAGE_PHONE_SERVICES_DATA
      );
      if (phoneServicesData) {
        Logger.debug('[LineSelection] PhoneServicesLineService — fallback: found Phone Services data in localStorage');
      }
    }

    if (!phoneServicesData?.data?.payload?.lines || phoneServicesData.data.payload.lines.length === 0) {
      Logger.info(
        '[LineSelection] PhoneServicesLineService — no lines available ' +
        '(Phone Services WebSocket event may not have arrived in MS Teams embedded browser). ' +
        'This is a known issue — the integration.phoneservices event may be delayed or not fire at all. ' +
        'Suggest: 1) Check browser console for cross-launch URL results, 2) Verify Webex Desktop is connected, 3) Check for subscription errors in logs'
      );
      return {
        currentSelection: CallerIdLineType.DEFAULT_CLID,
        lines: [],
      };
    }

    const psLines = phoneServicesData.data.payload.lines;
    Logger.info(`[LineSelection] PhoneServicesLineService — found ${psLines.length} lines from Phone Services`);

    // Log each line for debugging data mapping
    psLines.forEach((psLine, idx) => {
      Logger.debug(`[LineSelection]   Line ${idx}: label="${psLine.label}", name="${psLine.name}", number="${psLine.number}", GUID="${psLine.GUID}", status="${psLine.status}"`);
    });

    // Always default to first line — no cached selection.
    const lines: ILineSelectionInfo[] = psLines.map((psLine, idx) => ({
      lineIndex: idx,
      lineLabel: getMultiLineLabel(idx + 1), // getMultiLineLabel is 1-based
      title: getLineTitle(psLine.label, psLine.name, `Line ${idx + 1}`),
      number: psLine.number || '',
      lineType: idx === 0 ? CallerIdLineType.DEFAULT_CLID : CallerIdLineType.ADDITIONAL_NUMBER,
      isSelected: idx === 0,
      lineGuid: psLine.GUID || undefined,
    }));

    Logger.info(`[LineSelection] PhoneServicesLineService — mapped ${lines.length} lines, defaulting to L1 (${lines[0]?.title || 'N/A'})`);

    return { currentSelection: CallerIdLineType.DEFAULT_CLID, lines };
  }

  async setLine(line: ILineSelectionInfo): Promise<void> {
    // No-op for WXC: selection lives only in React state (useLineSelection hook).
    // There is no server-side API or integration protocol command to propagate
    // line selection to the Webex Desktop app — see design doc Section 23: Pending.
    Logger.info(`[LineSelection] PhoneServicesLineService — setLine: ${line.lineLabel} (${line.title}) — session-only, not persisted`);
  }
}
